/*
 * Cart 3D previews — "one last look" at every Dice Tower System item's
 * actual customization before checkout, on both the full cart page
 * (sections/main-cart-items.liquid) and the cart drawer (snippets/cart-
 * drawer.liquid). Reuses the same <product-model>/Shopify.ModelViewerUI
 * plumbing and applyFlatColor/hexToFactor technique already established in
 * dice-tower-configurator.js and dice-tower-shop.js — deliberately its own
 * small self-contained file rather than importing either of those (same
 * reasoning dice-tower-shop.js's own header gives: those files are live-
 * critical for their own checkout flows, not worth the shared-code
 * regression risk for a one-time win), since this runs on every page that
 * can show a cart, not just the configurator/shop pages.
 *
 * Per-item color data is embedded server-side by snippets/cart-item-3d-
 * preview.liquid as a small <script type="application/json"> block with
 * one of 4 "kind"s — see that snippet's own doc comment for why there are
 * 4 instead of 1:
 *   - "byName": bundle items (Tower/Vault/Tray/Clamp) — materialColorsJson
 *     is a JSON-encoded {materialName: hex} object; applied by matching
 *     each of the GLB's own material names.
 *   - "byIndexHex": à la carte zone/multicolor items (Tray Body, Clamp,
 *     every pattern insert) — hexListJson is a JSON-encoded array of hex
 *     strings in material-index order.
 *   - "byIndexName": the Standard Front Top Panel's 2-option Panel/Logo
 *     pair — colorNames is an array of color NAMES (not hex) in material-
 *     index order, needing the COLOR_HEX lookup below.
 *   - "flatName": every other à la carte exact-match single-option item
 *     (Back Panel, vault Base/Lid, Crest Emblem, etc) — colorName is one
 *     color NAME applied to every material on the part.
 *
 * Cart content is replaced wholesale (innerHTML swap of a freshly server-
 * rendered section fragment — see cart-drawer.js's renderContents) on
 * every add/update/remove, which is exactly when Dawn's own cart-update.js
 * family publishes PUB_SUB_EVENTS.cartUpdate — AFTER their own re-render,
 * confirmed by reading cart.js/product-form.js/quick-add-bulk.js/quick-
 * order-list.js/standard-actions-override.js, every one of which renders
 * first and publishes second. Subscribing to that event (instead of e.g. a
 * MutationObserver) means this always sees the fresh DOM already in place.
 */
(function () {
  'use strict';

  var COLORS = [
    { name: 'Jade White', hex: '#FFFFFF' },
    { name: 'Gold', hex: '#E4BD68' },
    { name: 'Silver', hex: '#A6A9AA' },
    { name: 'Gray', hex: '#8E9089' },
    { name: 'Bronze', hex: '#847D48' },
    { name: 'Brown', hex: '#9D432C' },
    { name: 'Red', hex: '#C12E1F' },
    { name: 'Pink', hex: '#F55A74' },
    { name: 'Orange', hex: '#FF6A13' },
    { name: 'Yellow', hex: '#F4EE2A' },
    { name: 'Bambu Green', hex: '#00AE42' },
    { name: 'Cyan', hex: '#0086D6' },
    { name: 'Blue', hex: '#0A2989' },
    { name: 'Purple', hex: '#5E43B7' },
    { name: 'Black', hex: '#000000' }
  ];
  var COLOR_HEX = {};
  COLORS.forEach(function (c) { COLOR_HEX[c.name] = c.hex; });

  function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function hexToFactor(hex) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return [1, 1, 1, 1];
    return [
      srgbToLinear(parseInt(m[1], 16) / 255),
      srgbToLinear(parseInt(m[2], 16) / 255),
      srgbToLinear(parseInt(m[3], 16) / 255),
      1
    ];
  }

  function applyFlatColor(material, hex) {
    try {
      if (material.pbrMetallicRoughness.baseColorTexture) {
        material.pbrMetallicRoughness.baseColorTexture.setTexture(null);
      }
    } catch (e) { /* no texture to strip — fine */ }
    material.pbrMetallicRoughness.setBaseColorFactor(hexToFactor(hex));
  }

  // model-viewer pauses its internal render loop while off-screen and can
  // silently reset its live camera to the library default on resume — see
  // the identical note in dice-tower-shop.js's recommitCameraOrbit, which
  // this mirrors exactly.
  function recommitCameraOrbit(mv) {
    if (!mv || typeof mv.jumpCameraToGoal !== 'function') return;
    var orbit = mv.getAttribute('camera-orbit');
    var framesLeft = 12;
    function reassert() {
      if (orbit) mv.cameraOrbit = orbit;
      mv.jumpCameraToGoal();
      framesLeft--;
      if (framesLeft > 0) requestAnimationFrame(reassert);
    }
    reassert();
  }

  function applyColors(mv, data) {
    if (!mv || !mv.model) return;
    try {
      var materialColors = data.kind === 'byName' ? JSON.parse(data.materialColorsJson) : null;
      var hexList = data.kind === 'byIndexHex' ? JSON.parse(data.hexListJson) : null;
      mv.model.materials.forEach(function (material, i) {
        var hex = null;
        if (data.kind === 'byName') {
          hex = materialColors[material.name];
        } else if (data.kind === 'byIndexHex') {
          hex = hexList[i];
        } else if (data.kind === 'byIndexName') {
          hex = COLOR_HEX[data.colorNames[i]];
        } else if (data.kind === 'flatName') {
          hex = COLOR_HEX[data.colorName];
        }
        if (hex) applyFlatColor(material, hex);
      });
    } catch (e) {
      // Malformed/missing color data — leave the model at its baked
      // default rather than throwing and breaking the rest of the cart.
    }
  }

  function loadAndApply(container) {
    if (container.dataset.diceCartInited) return;
    container.dataset.diceCartInited = 'true';

    var dataEl = container.querySelector('[data-cart-3d-data]');
    var productModel = container.querySelector('product-model');
    if (!dataEl || !productModel) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    if (!productModel.getAttribute('loaded')) productModel.loadContent(false);

    var waitForViewer = function () {
      var mv = productModel.querySelector('model-viewer');
      if (!mv) { requestAnimationFrame(waitForViewer); return; }
      mv.addEventListener('model-visibility', function (e) {
        if (e.detail && e.detail.visible) recommitCameraOrbit(mv);
      });
      if (mv.model) {
        recommitCameraOrbit(mv);
        applyColors(mv, data);
        return;
      }
      mv.addEventListener('load', function () {
        recommitCameraOrbit(mv);
        applyColors(mv, data);
      }, { once: true });
    };
    waitForViewer();
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      loadAndApply(entry.target);
    });
  }, { rootMargin: '200px 0px' });

  function scan() {
    document.querySelectorAll('[data-cart-3d-preview]').forEach(function (el) {
      if (el.dataset.diceCartInited) return;
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Cart drawer/page content is replaced wholesale on every cart mutation
  // — re-scan afterward so newly-added or newly-visible preview elements
  // get picked up. See this file's own header comment for why this event
  // (not a MutationObserver) is the right hook.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      requestAnimationFrame(scan);
    });
  }
})();
